/**
 * Session aggregates (§11, §29).
 *
 * `eventsPerHour` returns null for windows shorter than a configurable minimum. A 30-second
 * observation containing one event is not "120 events per hour", and presenting it that way
 * would be the single easiest route for this product to mislead someone.
 */

import {
  isConclusive,
  type AttentionEvent,
  type AttentionEventStats,
  type AttentionState,
} from '../models.js';

/** Below five minutes of observation, a per-hour rate is extrapolation, not measurement. */
export const MIN_WINDOW_FOR_RATE_SECONDS = 300;

export class EventAccumulator {
  private readonly minWindowForRate: number;
  private durations: number[] = [];
  private confidences: number[] = [];
  private directions = new Map<string, number>();
  private severities = new Map<string, number>();
  private types = new Map<string, number>();
  private observedSeconds = 0;
  private minorShifts = 0;

  constructor(minWindowForRateSeconds = MIN_WINDOW_FOR_RATE_SECONDS) {
    this.minWindowForRate = minWindowForRateSeconds;
  }

  add(event: AttentionEvent): void {
    this.types.set(event.eventType, (this.types.get(event.eventType) ?? 0) + 1);
    if (event.eventType !== 'attention_away') {
      // Face-lost and quality events are counted by type but excluded from the
      // attention-away statistics: mixing "could not measure" into "was measured looking
      // away" would inflate a number that carries consequences for a person.
      return;
    }
    if (event.severity === 'minor_shift') {
      this.minorShifts += 1;
      return;
    }
    this.durations.push(event.durationSeconds);
    this.confidences.push(event.confidence);
    this.directions.set(event.direction, (this.directions.get(event.direction) ?? 0) + 1);
    this.severities.set(event.severity, (this.severities.get(event.severity) ?? 0) + 1);
  }

  extend(events: Iterable<AttentionEvent>): void {
    for (const event of events) this.add(event);
  }

  /** Record sub-threshold shifts observed by the detector but not emitted. */
  noteMinorShifts(count: number): void {
    this.minorShifts = Math.max(this.minorShifts, count);
  }

  setObservedWindow(seconds: number): void {
    this.observedSeconds = Math.max(0, seconds);
  }

  get minorShiftCount(): number {
    return this.minorShifts;
  }

  get typeDistribution(): Record<string, number> {
    return Object.fromEntries(this.types);
  }

  snapshot(): AttentionEventStats {
    const count = this.durations.length;
    const total = this.durations.reduce((sum, value) => sum + value, 0);
    const eventsPerHour =
      this.observedSeconds >= this.minWindowForRate ? count / (this.observedSeconds / 3600) : null;

    return {
      eventCount: count,
      totalDurationSeconds: total,
      averageDurationSeconds: count > 0 ? total / count : 0,
      longestDurationSeconds: count > 0 ? Math.max(...this.durations) : 0,
      eventsPerHour,
      directionDistribution: Object.fromEntries(this.directions),
      severityDistribution: Object.fromEntries(this.severities),
      observedWindowSeconds: this.observedSeconds,
      meanConfidence:
        this.confidences.length > 0
          ? this.confidences.reduce((sum, value) => sum + value, 0) / this.confidences.length
          : 0,
    };
  }
}

/**
 * Accumulate time in each attention state.
 *
 * Time is credited only between two observed samples, so a gap in monitoring (laptop asleep,
 * camera unplugged) contributes to no state at all rather than being attributed to whichever
 * state happened to be current. `maxCreditSeconds` bounds a single interval for the same
 * reason — and the dropped time is reported, so the denominator is always honest.
 */
export class StateAccumulator {
  private readonly maxCredit: number;
  private totalsByState = new Map<AttentionState, number>();
  private lastState: AttentionState | null = null;
  private lastTs: number | null = null;
  private droppedSeconds = 0;
  private transitions = 0;

  constructor(maxCreditSeconds = 5) {
    this.maxCredit = maxCreditSeconds;
  }

  observe(state: AttentionState, monotonicTs: number): void {
    if (this.lastState !== null && this.lastTs !== null) {
      const delta = monotonicTs - this.lastTs;
      if (delta > 0) {
        if (delta <= this.maxCredit) {
          this.totalsByState.set(
            this.lastState,
            (this.totalsByState.get(this.lastState) ?? 0) + delta,
          );
        } else {
          this.droppedSeconds += delta;
        }
      }
      if (state !== this.lastState) this.transitions += 1;
    }
    this.lastState = state;
    this.lastTs = monotonicTs;
  }

  get totals(): Map<AttentionState, number> {
    return new Map(this.totalsByState);
  }

  /** Total time actually credited to a state — the honest denominator. */
  get monitoredSeconds(): number {
    let total = 0;
    for (const seconds of this.totalsByState.values()) total += seconds;
    return total;
  }

  /** Elapsed time that could not be credited (gaps in monitoring). */
  get unmonitoredSeconds(): number {
    return this.droppedSeconds;
  }

  get transitionCount(): number {
    return this.transitions;
  }

  secondsIn(state: AttentionState): number {
    return this.totalsByState.get(state) ?? 0;
  }

  fractionIn(state: AttentionState): number | null {
    const monitored = this.monitoredSeconds;
    if (monitored <= 0) return null;
    return (this.totalsByState.get(state) ?? 0) / monitored;
  }

  toJson(): Record<string, unknown> {
    const monitored = this.monitoredSeconds;
    let conclusive = 0;
    for (const [state, seconds] of this.totalsByState) {
      if (isConclusive(state)) conclusive += seconds;
    }
    return {
      monitored_seconds: round(monitored, 1),
      unmonitored_seconds: round(this.droppedSeconds, 1),
      state_transitions: this.transitions,
      seconds_by_state: Object.fromEntries(
        [...this.totalsByState.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([state, seconds]) => [state, round(seconds, 1)]),
      ),
      conclusive_fraction: monitored <= 0 ? null : round(conclusive / monitored, 3),
    };
  }
}

function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}
